import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { CATEGORIES, formatDate, CategoryBadge } from "@/lib/categories";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
    const [posts, setPosts] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        let alive = true;
        axios.get(`${API}/posts`, { params: { exclude_category: "news" } }).then(({ data }) => {
            if (alive) setPosts(data);
        }).catch(() => {
            if (alive) setPosts([]);
        });
        return () => { alive = false; };
    }, []);

    useSEO({
        title: "Insights & Field Notes | Chesterton",
        description:
            "Plain-spoken legal insights on estate planning, real estate, and business law for landowners, families, and closely-held businesses.",
        path: "/blog",
        schema: posts && posts.length ? {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Chesterton Insights",
            description: "Estate planning, real estate, and business law insights",
            blogPost: posts.map((p) => ({
                "@type": "BlogPosting",
                headline: p.title,
                datePublished: p.date,
                description: p.excerpt
            }))
        } : null
    });

    const filtered = useMemo(() => {
        if (!posts) return [];
        if (filter === "all") return posts;
        return posts.filter((p) => p.category === filter);
    }, [filter, posts]);

    const featured = posts && posts.length ? posts[0] : null;
    const rest = featured ? filtered.filter((p) => p.slug !== featured.slug) : filtered;

    return (
        <>
            <section className="bg-acre-bg" data-testid="blog-hero">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 lg:pt-24 pb-12">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-acre-secondary mb-5">
                        Insights & Field Notes
                    </div>
                    <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.98] tracking-tight text-acre-secondary max-w-4xl">
                        Plain-spoken thinking on the law that touches your land, your family, and your business.
                    </h1>
                    <p className="mt-7 max-w-2xl text-base lg:text-lg leading-relaxed text-acre-muted">
                        Practical notes from our practice, written for the people who actually have to make these decisions.
                    </p>
                </div>
            </section>

            {posts === null && (
                <div className="text-center text-acre-muted py-20" data-testid="blog-loading">Loading…</div>
            )}

            {featured && filter === "all" && (
                <section className="bg-acre-bg pb-12" data-testid="blog-featured">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                        <Link
                            to={`/blog/${featured.slug}`}
                            data-testid={`featured-post-${featured.slug}`}
                            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-white border border-acre-line p-6 lg:p-10 hover:-translate-y-1 hover:shadow-sm transition-all"
                        >
                            <div className="lg:col-span-6 relative aspect-[4/3] overflow-hidden bg-acre-line">
                                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            </div>
                            <div className="lg:col-span-6 flex flex-col justify-center">
                                <div className="flex items-center gap-3 mb-5">
                                    <CategoryBadge category={featured.category} />
                                    <span className="text-[10px] uppercase tracking-[0.25em] text-acre-muted">Featured</span>
                                </div>
                                <h2 className="font-serif-display text-3xl lg:text-5xl text-acre-secondary leading-[1.05]">
                                    {featured.title}
                                </h2>
                                <p className="mt-5 text-acre-muted text-base lg:text-lg leading-relaxed">{featured.excerpt}</p>
                                <div className="mt-7 flex items-center gap-5 text-xs text-acre-muted">
                                    <span className="flex items-center gap-2"><Calendar size={13} /> {formatDate(featured.date)}</span>
                                    <span className="flex items-center gap-2"><Clock size={13} /> {featured.read_time}</span>
                                </div>
                                <div className="mt-7 text-sm text-acre-primary font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                                    Read article <ArrowRight size={16} />
                                </div>
                            </div>
                        </Link>
                    </div>
                </section>
            )}

            {posts && posts.length > 0 && (
                <section className="py-16 lg:py-20 bg-acre-bg">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                        <div className="flex flex-wrap items-center gap-2 mb-12 border-b border-acre-line pb-6">
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted mr-3">Filter</span>
                            <button
                                data-testid="filter-all"
                                onClick={() => setFilter("all")}
                                className={`text-sm px-4 py-2 border transition-all ${filter === "all" ? "bg-acre-primary text-white border-acre-primary" : "bg-transparent text-acre-ink border-acre-line hover:border-acre-primary"}`}
                            >
                                All
                            </button>
                            {Object.entries(CATEGORIES).map(([key, c]) => (
                                <button
                                    key={key}
                                    data-testid={`filter-${key}`}
                                    onClick={() => setFilter(key)}
                                    className={`text-sm px-4 py-2 border transition-all ${filter === key ? "bg-acre-primary text-white border-acre-primary" : "bg-transparent text-acre-ink border-acre-line hover:border-acre-primary"}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>

                        <div data-testid="blog-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {(filter === "all" ? rest : filtered).map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`post-card-${p.slug}`}
                                    className="group bg-white border border-acre-line overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-sm transition-all"
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden bg-acre-line">
                                        <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <div className="p-7 flex flex-col flex-1">
                                        <CategoryBadge category={p.category} />
                                        <h3 className="font-serif-display text-2xl text-acre-ink leading-snug mt-4 group-hover:text-acre-secondary transition-colors">
                                            {p.title}
                                        </h3>
                                        <p className="mt-4 text-sm leading-relaxed text-acre-muted flex-1">{p.excerpt}</p>
                                        <div className="mt-6 pt-5 border-t border-acre-line flex items-center justify-between text-xs text-acre-muted">
                                            <span className="flex items-center gap-2"><Calendar size={12} /> {formatDate(p.date)}</span>
                                            <span className="flex items-center gap-2"><Clock size={12} /> {p.read_time}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {filtered.length === 0 && (
                            <div className="text-center text-acre-muted py-20" data-testid="blog-empty">
                                No posts in this category yet.
                            </div>
                        )}
                    </div>
                </section>
            )}

            <CTABanner />
        </>
    );
}
